import React from "react";
import * as Sentry from "@sentry/react";

/**
 * Ловит ошибки маршрутов, включая срыв загрузки ленивого чанка.
 * После деплоя хеши файлов меняются, старые чанки исчезают — открытая у пользователя
 * вкладка при переходе получает отказ импорта. Без этой границы React снёс бы всё
 * дерево и оставил белый экран вместо страницы.
 *
 * Namely Sentry.ErrorBoundary здесь использовать нельзя: при сборке он не даёт
 * пререндеру дождаться события prerender-ready, и сборка падает по таймауту.
 * Поэтому граница своя, а Sentry дёргаем вручную уже при перехвате.
 */
const RouteErrorFallback: React.FC = () => (
  <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
    <h2 className="text-xl font-semibold text-foreground">Не удалось загрузить страницу</h2>
    <p className="max-w-md text-muted-foreground">
      Скорее всего, приложение обновилось, пока страница была открыта. Обновите её — данные не потеряются.
    </p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="rounded-md bg-primary px-5 py-2.5 font-medium text-primary-foreground transition-opacity hover:opacity-90"
    >
      Обновить страницу
    </button>
  </div>
);

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
}

export class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("Route render failed", error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  render(): React.ReactNode {
    return this.state.hasError ? <RouteErrorFallback /> : this.props.children;
  }
}
